/* eslint-disable max-lines */
import Swal from "sweetalert2";

const DIRECT_PROFILE_ROUTE_TYPES = {
  personal_profile_direct: "personal",
  business_profile_direct: "business",
};

export function get_id_from_profile_slug(slug) {
  return String(slug || "").match(/-(\d+)$/)?.[1] || "";
}

function first_present(values) {
  return values.find((value) => value !== undefined && value !== null && String(value).trim() !== "") || "";
}

function get_profile_type_from_path(path) {
  return String(path || "").match(/^\/profile\/([^/]+)/)?.[1] || "";
}

function get_profile_type_from_route(route) {
  return DIRECT_PROFILE_ROUTE_TYPES[route.name] || get_profile_type_from_path(route.path);
}

function get_route_user_id(route, profile_type, profile_slug) {
  const slug_id = get_id_from_profile_slug(profile_slug);
  return first_present([
    route.query.user_id,
    profile_type === "personal" ? slug_id : "",
  ]);
}

export function get_profile_route_context(route) {
  const profile_slug = first_present([route.params.profile_slug, route.query.profile]);
  const profile_type = first_present([get_profile_type_from_route(route), route.query.type]);

  return {
    profile_slug,
    profile_type,
    user_id: get_route_user_id(route, profile_type, profile_slug),
  };
}

function get_query_base_payload(route) {
  return route.query.base ? { base_profile_id: route.query.base } : null;
}

function get_business_slug_payload(profile_type, profile_slug) {
  return profile_type === "business" && profile_slug
    ? { base_profile_id: get_id_from_profile_slug(profile_slug) }
    : null;
}

function get_slug_payload(profile_type, profile_slug) {
  return profile_slug ? { profile_slug, profile_type: profile_type || "personal" } : null;
}

function get_user_payload(user_id) {
  return user_id ? { user_id } : {};
}

export function get_profile_request_payload({ route, profile_type, profile_slug, user_id }) {
  return ({
    ...get_query_base_payload(route),
    ...get_business_slug_payload(profile_type, profile_slug),
    ...get_slug_payload(profile_type, profile_slug),
    ...get_user_payload(user_id),
  });
}

function is_plain_object(value) {
  return Boolean(value) && value.constructor === Object;
}

function get_social_link_url(link = {}) {
  return String(link.url || "").trim();
}

function get_saved_social_link_url(link = {}) {
  return String(link.social_link || "").trim();
}

function create_empty_social_link() {
  return ({
    id: null,
    platform: "",
    url: "",
  });
}

function build_editable_social_link(link = {}) {
  return ({
    id: link.id || null,
    platform: link.social_type?.id || "",
    url: get_saved_social_link_url(link),
  });
}

function has_social_platform(link = {}) {
  return Boolean(link.platform);
}

function has_social_url(link = {}) {
  return get_social_link_url(link) !== "";
}

function has_social_link_value(link = {}) {
  return has_social_platform(link) || has_social_url(link);
}

function is_complete_social_link(link = {}) {
  return has_social_platform(link) && has_social_url(link);
}

function is_incomplete_social_link(link = {}) {
  return has_social_link_value(link) && !is_complete_social_link(link);
}

function has_social_link_id(link = {}) {
  return Boolean(link.id);
}

function get_saved_social_profiles(response = {}) {
  return Array.isArray(response.data) ? response.data : [response.data];
}

function social_profiles_match(profile_link = {}, saved_link = {}) {
  return profile_link.id === saved_link.id
    || profile_link.social_type?.id === saved_link.social_type?.id;
}

function get_existing_social_profile_index(profile_links = [], saved_link = {}) {
  return profile_links.findIndex((profile_link) => social_profiles_match(profile_link, saved_link));
}

export const customer_profile_social_methods = {
  get_empty_social_link() {
    return create_empty_social_link();
  },
  get_editable_social_links() {
    const existing_links = Array.isArray(this.profileData?.social_profiles)
      ? this.profileData.social_profiles.map(build_editable_social_link)
      : [];

    return existing_links.length ? existing_links : [this.get_empty_social_link()];
  },
  reset_social_links_editor() {
    this.errors.social_link = "";
    this.new_socials = this.get_editable_social_links();
  },
  open_social_modal() {
    this.reset_social_links_editor();
    $("#social_modal").modal("show");
  },
  ensure_social_editor_has_row() {
    if (!this.new_socials.length) {
      this.new_socials.push(this.get_empty_social_link());
    }
  },
  show_social_links_toast({ title, text, icon = "success" }) {
    Swal.fire({
      title,
      text,
      icon,
      timer: 1800,
      showConfirmButton: false,
      position: "bottom-end",
      toast: true,
    });
  },
  has_pending_social_links() {
    return this.new_socials.some(has_social_link_value);
  },
  has_incomplete_social_links() {
    return this.new_socials.some(is_incomplete_social_link);
  },
  get_social_profile_save_settings(options = {}) {
    const settings = is_plain_object(options) ? options : {};

    return ({
      require_link: settings.require_link !== false,
      hide_modal: settings.hide_modal !== false,
      show_error: settings.show_error !== false,
    });
  },
  get_valid_social_links() {
    return this.new_socials.filter(is_complete_social_link);
  },
  reject_incomplete_social_links() {
    this.errors.social_link = "Please complete the platform and link for each social profile.";
    return false;
  },
  accept_social_links() {
    this.errors.social_link = "";
    return true;
  },
  resolve_empty_social_links(settings) {
    this.set_empty_social_link_error(settings);
    return !settings.require_link;
  },
  set_empty_social_link_error(settings) {
    if (!settings.require_link || !settings.show_error) return;
    this.errors.social_link = "Please enter at least one valid social link.";
  },
  validate_social_profile_links(valid_links, settings) {
    if (this.has_incomplete_social_links()) {
      return this.reject_incomplete_social_links();
    }

    if (valid_links.length > 0) {
      return this.accept_social_links();
    }

    return this.resolve_empty_social_links(settings);
  },
  build_social_profile_payload(valid_links) {
    return ({
      base_profile_id: this.profileData?.base_profile?.id,
      social_profiles: valid_links.map(link => ({
        social_type_id: link.platform,
        social_link: get_social_link_url(link)
      })),
    });
  },
  ensure_profile_social_links() {
    if (!this.profileData?.social_profiles) {
      this.profileData.social_profiles = [];
    }
  },
  upsert_profile_social_link(saved_link) {
    const existing_index = get_existing_social_profile_index(this.profileData.social_profiles, saved_link);

    if (existing_index === -1) {
      this.profileData.social_profiles.push(saved_link);
      return;
    }

    this.profileData.social_profiles.splice(existing_index, 1, saved_link);
  },
  upsert_profile_social_links(saved_profiles = []) {
    this.ensure_profile_social_links();
    saved_profiles.forEach((saved_link) => {
      this.upsert_profile_social_link(saved_link);
    });
  },
  remove_profile_social_link(social_profile_id) {
    this.ensure_profile_social_links();
    this.profileData.social_profiles = this.profileData.social_profiles.filter(
      (link) => String(link.id) !== String(social_profile_id)
    );
  },
  close_social_modal_if_needed(hide_modal) {
    if (!hide_modal) return;
    $("#social_modal").modal("hide");
  },
  handle_social_profiles_save_success(response, hide_modal, feedback_text = "Social links saved successfully.") {
    const saved_profiles = get_saved_social_profiles(response);
    this.upsert_profile_social_links(saved_profiles);
    this.reset_social_links_editor();
    this.close_social_modal_if_needed(hide_modal);
    this.show_social_links_toast({
      title: "Success",
      text: feedback_text,
      icon: "success",
    });
  },
  handle_social_profiles_save_response(response, settings, resolve, feedback_text) {
    if (response.status !== "success") {
      resolve(false);
      return;
    }

    this.handle_social_profiles_save_success(response, settings.hide_modal, feedback_text);
    resolve(true);
  },
  create_social_profiles(payload) {
    return new Promise((resolve, reject) => {
      $.ajax({
        type: "POST",
        url: `${process.env.VUE_APP_BACKEND_API_URL}/company_management/create_social_profile/`,
        headers: {
          Authorization: `Token ${this.getToken()}`,
        },
        data: JSON.stringify(payload),
        contentType: "application/json",
        success: resolve,
        error: reject,
      });
    });
  },
  update_social_profile_link(link) {
    return new Promise((resolve, reject) => {
      $.ajax({
        type: "POST",
        url: `${process.env.VUE_APP_BACKEND_API_URL}/company_management/update_social_profile/`,
        headers: {
          Authorization: `Token ${this.getToken()}`,
        },
        data: JSON.stringify({
          social_profile_id: link.id,
          base_profile_id: this.profileData?.base_profile?.id,
          social_type_id: link.platform,
          social_link: get_social_link_url(link),
        }),
        contentType: "application/json",
        success: resolve,
        error: reject,
      });
    });
  },
  delete_social_profile_link(social_profile_id) {
    return new Promise((resolve, reject) => {
      $.ajax({
        type: "DELETE",
        url: `${process.env.VUE_APP_BACKEND_API_URL}/company_management/delete_social_profile/`,
        headers: {
          Authorization: `Token ${this.getToken()}`,
        },
        data: JSON.stringify({
          social_profile_id,
          base_profile_id: this.profileData?.base_profile?.id,
        }),
        contentType: "application/json",
        success: resolve,
        error: reject,
      });
    });
  },
  save_social_profiles(options = {}) {
    const settings = this.get_social_profile_save_settings(options);
    const valid_links = this.get_valid_social_links();
    const is_valid = this.validate_social_profile_links(valid_links, settings);

    if (!is_valid || !valid_links.length) {
      return Promise.resolve(is_valid);
    }

    const payload = this.build_social_profile_payload(valid_links);
    const new_links = valid_links.filter((link) => !has_social_link_id(link));
    const existing_links = valid_links.filter(has_social_link_id);
    const requests = [];

    if (new_links.length) {
      requests.push(this.create_social_profiles({
        ...payload,
        social_profiles: new_links.map((link) => ({
          social_type_id: link.platform,
          social_link: get_social_link_url(link),
        })),
      }));
    }

    existing_links.forEach((link) => {
      requests.push(this.update_social_profile_link(link));
    });

    const feedback_text = new_links.length && existing_links.length
      ? "Social links created and updated successfully."
      : new_links.length
        ? "Social links created successfully."
        : "Social links updated successfully.";

    return new Promise((resolve) => {
      Promise.all(requests)
        .then((responses) => {
          const saved_profiles = responses.flatMap((response) => get_saved_social_profiles(response));
          this.handle_social_profiles_save_success({ data: saved_profiles }, settings.hide_modal, feedback_text);
          resolve(true);
        })
        .catch(() => resolve(false));
    });
  },
};
